import { IAutocompleteBaseOption } from '../types'

type TPickedProps =
  | 'groupIndex'
  | 'groupName'
  | 'iconName'
  | 'optionType'
  | 'dividerAfter'
  | 'dividerBefore'
  | 'iconColor'

export interface IGetAutocompleteOptionsConfig<T>
  extends Pick<IAutocompleteBaseOption, TPickedProps> {
  /**
   * {T} type is canonical generic which also sets M
   */
  options: T[]
  /**
   * Default index used for ordering groups in ascending order
   */
  groupIndex: number
  /**
   * Input value that is used to render custom options
   */
  inputValue?: string
  /**
   * Uses just `text` and `id` props to map an array.
   * @note optionally can include any other property from
   * `IAutocompleteOption`, but requires that it be mapped and rendered
   * with a custom `renderOptionType` prop
   * @example
   * ```
   * const mapOptionProp = ({ name: text, id }) => ({
   *   text,
   *   id
   * })
   * ```
   */
  mapOption: (option: T, index: number) => Omit<IAutocompleteBaseOption, 'groupIndex'>
}

/**
 * Compose options array for <Autocomplete />
 * @example
 *  const customerOptions = getAutocompleteOptions({
 *    options: customerList,
 *    groupIndex: 1,
 *    groupName: 'Customers',
 *    iconName: 'PlusIcon',
 *    inputValue: inputValue,
 *    mapOption: ({ name: text, id = '' }) => ({ text, id }),
 *  })
 */
function getAutocompleteOptions<T>(
  config: IGetAutocompleteOptionsConfig<T>
): IAutocompleteBaseOption[] {
  const {
    options = [],
    mapOption,
    groupIndex,
    groupName = '',
    iconName,
    iconColor,
    optionType = 'default',
    dividerBefore = false,
    dividerAfter = false,
  } = config || {}
  return options.map(mapOption).map(o => ({
    ...o,
    groupIndex,
    groupName,
    iconName,
    iconColor,
    optionType,
    dividerBefore,
    dividerAfter,
  }))
}

export default getAutocompleteOptions
